import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Palette as PaletteIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon
} from '@mui/icons-material';
import { HexColorPicker } from 'react-colorful';
import linksApi from '../api/linksApi';

const QRCodeCustomizer = ({ 
  open, 
  onClose, 
  linkId, 
  linkName, 
  onCustomQRGenerated 
}) => {
  // QR Design Options State
  const [qrOptions, setQROptions] = useState({
    size: 256,
    quality: 0.92,
    margin: 2,
    foregroundColor: '#000000',
    backgroundColor: '#FFFFFF',
    errorCorrection: 'M',
    format: 'png',
    theme: 'default'
  });

  // UI State
  const [isGenerating, setIsGenerating] = useState(false);
  const [customQRData, setCustomQRData] = useState(null);
  const [showForegroundColorPicker, setShowForegroundColorPicker] = useState(false);
  const [showBackgroundColorPicker, setShowBackgroundColorPicker] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Predefined color themes
  const colorThemes = {
    default: { 
      name: 'Default', 
      foreground: '#000000', 
      background: '#FFFFFF',
      description: 'Classic black on white'
    },
    brand: { 
      name: 'Brand Green', 
      foreground: '#00cc66', 
      background: '#FFFFFF',
      description: 'Brand green color scheme'
    },
    blue: { 
      name: 'Professional Blue', 
      foreground: '#1976d2', 
      background: '#FFFFFF',
      description: 'Professional blue theme'
    },
    dark: { 
      name: 'Dark Mode', 
      foreground: '#FFFFFF', 
      background: '#1a1a1a',
      description: 'Dark theme with white QR code'
    },
    purple: { 
      name: 'Purple', 
      foreground: '#9c27b0', 
      background: '#FFFFFF',
      description: 'Purple accent color'
    },
    orange: { 
      name: 'Orange', 
      foreground: '#ff9800', 
      background: '#FFFFFF',
      description: 'Orange accent color'
    }
  };

  // Generate custom QR code
  const generateCustomQR = async () => {
    setIsGenerating(true);
    setError('');
    setSuccess('');

    try {
      const response = await linksApi.getCustomQRCode(linkId, qrOptions);
      setCustomQRData(response);
      setSuccess('Custom QR code generated successfully!');
      
      // Callback to parent component
      if (onCustomQRGenerated) {
        onCustomQRGenerated(response);
      }
    } catch (error) {
      console.error('Error generating custom QR code:', error);
      setError(error.message || 'Failed to generate custom QR code');
    } finally {
      setIsGenerating(false);
    }
  };

  // Apply color theme
  const applyColorTheme = (themeKey) => {
    const theme = colorThemes[themeKey];
    if (theme) {
      setQROptions(prev => ({
        ...prev,
        foregroundColor: theme.foreground,
        backgroundColor: theme.background,
        theme: themeKey
      }));
    }
  };

  // Download custom QR code
  const downloadCustomQR = () => {
    if (!customQRData?.dataUrl) return;

    try {
      // Use the linksApi helper instead of manual creation
      linksApi.downloadCustomQRCode(
        customQRData.dataUrl, 
        linkName, 
        qrOptions.format
      );
      setSuccess('Custom QR code downloaded successfully!');
    } catch (error) {
      console.error('Error downloading custom QR code:', error);
      setError(`Failed to download custom QR code: ${error.message}`);
    }
  };

  // Copy QR code data URL to clipboard
  const copyDataURL = async () => {
    if (!customQRData?.dataUrl) return;

    try {
      await navigator.clipboard.writeText(customQRData.dataUrl);
      setSuccess('Data URL copied to clipboard!');
    } catch (error) {
      setError('Failed to copy data URL');
    }
  };

  // Auto-generate QR code when options change
  useEffect(() => {
    if (open && linkId) {
      generateCustomQR();
    }
  }, [open, linkId, qrOptions]);

  // Clear messages after 3 seconds
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess('');
        setError('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        bgcolor: 'primary.main',
        color: 'white'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PaletteIcon />
          <Typography variant="h6">Custom QR Code Designer</Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* QR Code Preview */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  QR Code Preview
                </Typography>
                
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center',
                  minHeight: 280,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  position: 'relative'
                }}>
                  {isGenerating ? (
                    <CircularProgress size={40} />
                  ) : customQRData?.dataUrl ? (
                    <img 
                      src={customQRData.dataUrl} 
                      alt="Custom QR Code" 
                      style={{ 
                        maxWidth: '100%', 
                        maxHeight: 280,
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Typography color="text.secondary">
                      QR code will appear here
                    </Typography>
                  )}
                </Box>

                {/* QR Code Actions */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                  <Tooltip title="Regenerate QR code">
                    <IconButton 
                      onClick={generateCustomQR} 
                      disabled={isGenerating}
                      color="primary"
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download QR code">
                    <IconButton 
                      onClick={downloadCustomQR} 
                      disabled={!customQRData?.dataUrl}
                      color="primary"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Copy data URL">
                    <IconButton 
                      onClick={copyDataURL} 
                      disabled={!customQRData?.dataUrl}
                      color="primary"
                    >
                      <CopyIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* Link Information */}
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Link:</strong> {linkName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Size:</strong> {qrOptions.size}x{qrOptions.size}px
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Format:</strong> {qrOptions.format.toUpperCase()}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Customization Options */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Design Options
                </Typography>

                {/* Color Themes */}
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Color Themes
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {Object.entries(colorThemes).map(([key, theme]) => (
                      <Chip
                        key={key}
                        label={theme.name}
                        onClick={() => applyColorTheme(key)}
                        variant={qrOptions.theme === key ? 'filled' : 'outlined'}
                        color={qrOptions.theme === key ? 'primary' : 'default'}
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>

                {/* Custom Colors */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Foreground Color
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: qrOptions.foregroundColor,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => setShowForegroundColorPicker(!showForegroundColorPicker)}
                      />
                      <TextField
                        value={qrOptions.foregroundColor}
                        onChange={(e) => setQROptions(prev => ({ ...prev, foregroundColor: e.target.value }))}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    {showForegroundColorPicker && (
                      <Box sx={{ mt: 1 }}>
                        <HexColorPicker
                          color={qrOptions.foregroundColor}
                          onChange={(color) => setQROptions(prev => ({ ...prev, foregroundColor: color }))}
                          style={{ width: '100%' }}
                        />
                      </Box>
                    )}
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="subtitle2" gutterBottom>
                      Background Color
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          bgcolor: qrOptions.backgroundColor,
                          border: '1px solid',
                          borderColor: 'grey.300',
                          borderRadius: 1,
                          cursor: 'pointer'
                        }}
                        onClick={() => setShowBackgroundColorPicker(!showBackgroundColorPicker)}
                      />
                      <TextField
                        value={qrOptions.backgroundColor}
                        onChange={(e) => setQROptions(prev => ({ ...prev, backgroundColor: e.target.value }))}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Box>
                    {showBackgroundColorPicker && (
                      <Box sx={{ mt: 1 }}>
                        <HexColorPicker
                          color={qrOptions.backgroundColor}
                          onChange={(color) => setQROptions(prev => ({ ...prev, backgroundColor: color }))}
                          style={{ width: '100%' }}
                        />
                      </Box>
                    )}
                  </Grid>
                </Grid>

                {/* Size Slider */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Size: {qrOptions.size}px
                  </Typography>
                  <Slider
                    value={qrOptions.size}
                    onChange={(_, value) => setQROptions(prev => ({ ...prev, size: value }))}
                    min={128}
                    max={512}
                    step={32}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>

                {/* Quality Slider */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Quality: {Math.round(qrOptions.quality * 100)}%
                  </Typography>
                  <Slider
                    value={qrOptions.quality}
                    onChange={(_, value) => setQROptions(prev => ({ ...prev, quality: value }))}
                    min={0.1}
                    max={1.0}
                    step={0.1}
                    marks
                    valueLabelDisplay="auto"
                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                  />
                </Box>

                {/* Margin Slider */}
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Margin: {qrOptions.margin}px
                  </Typography>
                  <Slider
                    value={qrOptions.margin}
                    onChange={(_, value) => setQROptions(prev => ({ ...prev, margin: value }))}
                    min={0}
                    max={10}
                    step={1}
                    marks
                    valueLabelDisplay="auto"
                  />
                </Box>

                {/* Format Selection */}
                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Format</InputLabel>
                    <Select
                      value={qrOptions.format}
                      onChange={(e) => setQROptions(prev => ({ ...prev, format: e.target.value }))}
                      label="Format"
                    >
                      <MenuItem value="png">PNG</MenuItem>
                      <MenuItem value="jpeg">JPEG</MenuItem>
                      <MenuItem value="svg">SVG</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Error Correction */}
                <Box sx={{ mt: 3 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Error Correction</InputLabel>
                    <Select
                      value={qrOptions.errorCorrection}
                      onChange={(e) => setQROptions(prev => ({ ...prev, errorCorrection: e.target.value }))}
                      label="Error Correction"
                    >
                      <MenuItem value="L">Low (~7%)</MenuItem>
                      <MenuItem value="M">Medium (~15%)</MenuItem>
                      <MenuItem value="Q">Quartile (~25%)</MenuItem>
                      <MenuItem value="H">High (~30%)</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
        <Button 
          onClick={generateCustomQR} 
          variant="contained" 
          disabled={isGenerating}
          startIcon={isGenerating ? <CircularProgress size={20} /> : <RefreshIcon />}
        >
          {isGenerating ? 'Generating...' : 'Regenerate QR Code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRCodeCustomizer;
